import React, { useRef, useState } from "react";
import { CiSaveUp2 } from "react-icons/ci";

interface FileDropzoneProps {
  accept: string; // for the file picker, e.g. "audio/*" or ".pdf"
  // Only applied to dropped files: the file picker already filters by
  // `accept`, but a drop can be anything.
  isAcceptedFile: (file: File) => boolean;
  rejectMessage: (file: File) => string;
  onFile: (file: File) => void;
  hasFile: boolean;
  emptyTitle: string;
  emptyHint: string;
  inputLabel: string;
  invalid?: boolean;
  // Rendered instead of the empty drop area once there is a file; gets a
  // function that opens the file picker (for a "Replace" button).
  children?: (openPicker: () => void) => React.ReactNode;
}

// Upload area for the Add / Edit project page. Dropping a file and picking
// one go through the same path, so the preview always matches the file that
// will be uploaded. (UploadFile, used on the Post page, is left unchanged.)
const FileDropzone: React.FC<FileDropzoneProps> = ({
  accept,
  isAcceptedFile,
  rejectMessage,
  onFile,
  hasFile,
  emptyTitle,
  emptyHint,
  inputLabel,
  invalid,
  children,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  const openPicker = () => inputRef.current?.click();

  const takeFile = (file: File) => {
    setRejected(null);
    onFile(file);
  };

  return (
    <div
      className="flex flex-col gap-1.5"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        if (!isAcceptedFile(file)) {
          setRejected(rejectMessage(file));
          return;
        }
        takeFile(file);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        aria-label={inputLabel}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared so choosing the same file again still fires onChange.
          e.target.value = "";
          if (file) takeFile(file);
        }}
      />

      {hasFile && children ? (
        <div className={`rounded-[20px] transition-shadow ${dragging ? "ring-2 ring-primary ring-offset-2" : ""}`}>
          {children(openPicker)}
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className={`w-full border-2 border-dashed rounded-[20px] px-4 py-6 flex flex-col items-center gap-1.5 text-center transition-colors ${
            dragging
              ? "border-primary bg-primary/5"
              : invalid || rejected
                ? "border-red-primary/50 bg-[#fff8f8]"
                : "border-primary/25 bg-[#fbfcfa] hover:border-primary/40"
          }`}
        >
          <span className="w-12 h-12 rounded-full bg-primary/[0.07] text-primary grid place-items-center text-2xl">
            <CiSaveUp2 />
          </span>
          <span className="font-semibold text-[15px]">
            {dragging ? "Drop to use this file" : (
              <>
                {emptyTitle} <span className="text-primary underline underline-offset-[3px]">browse</span>
              </>
            )}
          </span>
          <span className="text-[13px] text-gray-70">{emptyHint}</span>
        </button>
      )}

      {rejected && (
        <p role="alert" className="text-sm font-semibold text-red-primary">
          {rejected}
        </p>
      )}
    </div>
  );
};

export default FileDropzone;
